import React, { useEffect, useRef, useState } from 'react';
import { Pencil, Radar } from 'lucide-react';
import type { IntlLike } from '../clientPresentation';
import { RECRUIT_AXES, useRecruitScores } from '../recruitScore';
import RecruitRadarChart from './RecruitRadarChart';
import RecruitScoreDialog from './RecruitScoreDialog';

/**
 * 候選人評估 — the 招募 view's radar, in the slot where 業務 shows 現有保障.
 * Five angles on the candidate, each scored 1–5 by the recruiter.
 *
 * Scores are read and written through ../recruitScore (saved on the
 * factsheet, `recruitment.scores`). The chart stays the chart alone:
 * the 總分, its band and the family warning are read in 招募 IDEA (which draws
 * the same RecruitRadarChart and opens the same RecruitScoreDialog), because on
 * a phone this half-width slot cannot carry them.
 */

export type RecruitRadarProps = {
  clientId: string;
  intl: IntlLike;
  /**
   * A changing number asks for the editor, and is how the client row's 雷達
   * shortcut arrives: from a row the ask is 「我要打分」, and scrolling the
   * panel to a pentagon the adviser then has to tap again is half an answer.
   * A token rather than a boolean so the same request can be made twice.
   *
   * Owned by the drawer (ClientDetailDrawer's `recruitScoreRequest`), not by
   * this component, and never reset back to 0 once a request has been served
   * — it is drawer-lifetime state, while THIS component unmounts and remounts
   * every time the drawer leaves and re-enters 招募 view (`recruitView` in
   * ClientDetailDrawer). So a mount can find a stale, already-handled token
   * sitting in `editRequest` from an earlier, unrelated ask. See the baseline
   * ref below.
   */
  editRequest?: number;
};

const RecruitRadar = (props: RecruitRadarProps) => {
  const { clientId, intl, editRequest } = props;
  const t = (id: string) => intl.formatMessage({ id });
  // Shared with the header's 評估 pill: a write in the dialog moves that pill too.
  const scores = useRecruitScores(clientId);
  const [editing, setEditing] = useState(false);

  // The drawer is reused across clients, so the editor closes when the person changes.
  useEffect(() => setEditing(false), [clientId]);

  /**
   * Whatever `editRequest` already reads on THIS mount is not a fresh ask —
   * it is either 0 or a token some earlier mount already opened the dialog
   * for. Baselining it here means the effect below only fires for a value
   * that arrives AFTER this instance exists, which is the actual event a
   * row's 雷達 shortcut produces. Without this, switching the drawer's own
   * 業務/招募 pill (or selecting a different candidate who defaults to 招募
   * view) remounts this component, sees the old token still sitting in
   * props, and pops the scoring dialog over a candidate nobody asked to
   * score.
   */
  const seenRequest = useRef(editRequest);
  useEffect(() => {
    if (editRequest && editRequest !== seenRequest.current) {
      seenRequest.current = editRequest;
      setEditing(true);
    }
  }, [editRequest]);

  const rated = RECRUIT_AXES.filter(axis => scores[axis] !== undefined);

  return (
    <div className="clientv2-radar clientv2-recruit-radar" data-testid="recruit-radar">
      <div className="clientv2-subsect clientv2-radar-legend">
        <Radar size={15} aria-hidden="true" />
        {t('clientv2-recruit-radar-title')}
        <button
          type="button"
          className="clientv2-recruit-radar-edit"
          data-testid="recruit-radar-edit"
          aria-haspopup="dialog"
          onClick={() => setEditing(true)}
        >
          <Pencil size={12} aria-hidden="true" />
          {t('clientv2-recruit-radar-edit')}
        </button>
      </div>

      <button
        type="button"
        className="clientv2-recruit-radar-chart"
        aria-label={t('clientv2-recruit-radar-edit')}
        onClick={() => setEditing(true)}
      >
        <RecruitRadarChart scores={scores} intl={intl} />
        {rated.length === 0 && (
          <span className="clientv2-recruit-radar-empty">{t('clientv2-recruit-radar-empty')}</span>
        )}
      </button>

      <RecruitScoreDialog open={editing} onClose={() => setEditing(false)} clientId={clientId} intl={intl} />
    </div>
  );
};

export default RecruitRadar;
